import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box, Stack, Typography } from '@mui/material';
import MUIContainer from '@mui/material/Container';
import { DateTime, Duration } from 'luxon';
import React, { type ForwardedRef, forwardRef } from 'react';
import { PieChart } from 'react-minimal-pie-chart';

import {
  AverageItem,
  AverageItemsStack,
  AverageReportContainer,
  Circle,
  Container,
  DayContainer,
  DayTitle,
  Header,
  HeaderLabel,
  HeaderText,
  HeaderTextContainer,
  Logo,
  OutlinedBox,
  Page,
  PageBreak,
  PatientDetails,
  PieChartContainer,
  Separator,
  TimelineBar,
  TimelineContainer,
  TimelineItem, TimelineText,
} from './elements.js';
import { groupLogsByHour } from './groupLogsByHour.js';
import { type DailyReport, type DurationReport, type HeaderReport, type LogDetails, type ReportDetails } from '../../api/index.js';
import logo from '../../assets/logo-small.svg';
import { CodeCategoryViewMode } from '../../models/index.js';
import { hideWord } from '../../utils/index.js';


interface LogsReportProps {
  data: DailyReport;
  showPatientName: boolean;
}

const DST_CHANGE_NOTE = '* DST change';

const PatientLogsReport = forwardRef((props: LogsReportProps, ref: ForwardedRef<HTMLDivElement>) => {
  const headerReport = (headerReport: HeaderReport) => {
    const isPercentage = headerReport.type === CodeCategoryViewMode.PERCENTAGE;
    const isCount = headerReport.type === CodeCategoryViewMode.COUNT;
    const value = headerReport.sum || 0;
    return (
      <OutlinedBox>
        <Stack direction={'row'} alignItems={'center'}>
          <Circle color={headerReport.color} />
          <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
            {headerReport.name}
          </Typography>
        </Stack>
        <Typography sx={{ ml: 2, textAlign: 'end', fontSize: '0.7rem' }}>
          {isPercentage ? value.toFixed(2) : value}{' '}
          {isPercentage ? '%' : isCount && (value > 1 || value === 0) ? 'times' : 'time'}
        </Typography>
      </OutlinedBox>
    );
  };

  const durationReport = (durationReport: DurationReport) => {
    return (
      <AverageItem key={durationReport.categoryId}>
        <Circle color={durationReport.color} />
        <Typography sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{durationReport.name}</Typography>
        <Typography sx={{ textAlign: 'right', fontSize: '0.7rem' }}>
          {Duration.fromObject({ milliseconds: parseInt(durationReport.sum) }).toFormat('hh:mm')}
        </Typography>
        <Typography sx={{ textAlign: 'right', fontSize: '0.7rem' }}>{durationReport.percentage}</Typography>
      </AverageItem>
    );
  };

  const timelineReport = (logs: LogDetails[], startTime: string, endTime: string) => {
    if (logs?.length === 0) {
      return null;
    }
    const hourlyLogs = groupLogsByHour(logs, startTime, endTime, props.data.timezone);
    const hasDstChange = hourlyLogs.some((hour) => hour.dstChange);
    return (
      <TimelineContainer>
        {hourlyLogs.map((hour) => {
          return (
            <TimelineItem>
              <TimelineText>{hour.dstChange ? `${hour.label}*` : hour.label}</TimelineText>
              <TimelineBar>
                {hour.logs.map((log) => {
                  const width = ((log.duration ?? 3600) / 3600) * 100;
                  return (
                    <div
                      style={{
                        backgroundColor: log.color,
                        width: width + '%',
                        height: '1rem',
                      }}
                    ></div>
                  );
                })}
                <Separator percentage={25} />
                <Separator percentage={50} />
                <Separator percentage={75} />
              </TimelineBar>
            </TimelineItem>
          );
        })}
        {hasDstChange ? (
          <Typography sx={{ mt: 1, fontSize: '0.7rem', color: 'text.secondary' }}>{DST_CHANGE_NOTE}</Typography>
        ) : null}
      </TimelineContainer>
    );
  };
  const dailyReport = (report: ReportDetails, index: number) => {
    const name = props.showPatientName ? props.data.patientName : hideWord(props.data.patientName);
    const patientId = props.showPatientName ? props.data.patientId : hideWord(props.data.patientId, '-');
    return (
      <Container key={report.eventId}>
        <PageBreak />
        <Page>
          <Header hidden={index !== 0}>
            <Logo>
              <img src={logo} alt="Onekeycare Logo" />
            </Logo>
            <PatientDetails>
              <HeaderTextContainer>
                <HeaderLabel>Name:</HeaderLabel>
                <HeaderText>{name}</HeaderText>
              </HeaderTextContainer>
              <HeaderTextContainer>
                <HeaderLabel>Client ID:</HeaderLabel>
                <HeaderText>{patientId}</HeaderText>
              </HeaderTextContainer>
            </PatientDetails>
          </Header>
          <DayContainer>
            <DayTitle>
              <CalendarMonthIcon />
              {DateTime.fromISO(report.startDate, { zone: props.data.timezone }).toFormat('DDDD hh:mm a')} -{' '}
              {DateTime.fromISO(report.endDate, { zone: props.data.timezone }).toFormat('DDDD hh:mm a')}
            </DayTitle>
            <Stack direction={'row'} useFlexGap justifyContent={'space-between'} spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {report.headerReport.filter((val) => +val.sum > 0).map(headerReport)}
            </Stack>
            <AverageReportContainer>
              {report.durationReport?.length ? (
                <Box>
                  <AverageItemsStack>
                    {report.durationReport.sort((a, b) => +b.sum - +a.sum).map(durationReport)}
                  </AverageItemsStack>
                </Box>
              ) : null}
              <PieChartContainer>
                <PieChart
                  lineWidth={40}
                  style={{ maxWidth: '70%', padding: '16p' }}
                  data={report.durationReport
                    .sort((a, b) => +b.sum - +a.sum)
                    .map((rep) => {
                      return {
                        title: rep.name,
                        value: parseInt(rep.sum),
                        color: rep.color,
                      };
                    })}
                />
              </PieChartContainer>
            </AverageReportContainer>
            {timelineReport(report.logs, report.startDate, report.endDate)}
          </DayContainer>
        </Page>
      </Container>
    );
  };

  return (
    <MUIContainer maxWidth={'lg'} ref={ref}>
      {props.data.report.map(dailyReport)}
    </MUIContainer>
  );
});

export default PatientLogsReport;
